'use client';

import React, { useMemo, useCallback, forwardRef, useState, useRef } from 'react';
import styled from 'styled-components';
import {
  ResponsiveContainer,
  LineChart,
  BarChart,
  AreaChart,
  PieChart,
  ScatterChart,
  Line,
  Bar,
  Area,
  Pie,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
  ReferenceLine,
  Brush,
} from 'recharts';
import { theme } from '../../config/theme';
import { Typography } from '../ui-components/Typography/Typography';
import { CustomLegend } from './combined/CustomLegend';
import { ChartSize, ChartContainerProps, ChartTheme, ChartConfig, ChartData, RechartsDataPoint, ChartLayout } from '../../types/chart';

const ChartContainer = styled.div<{ $size: ChartSize }>`
  overflow: visible;
  transition: box-shadow 0.2s ease;

  /* Disable hover effect */
  /* &:hover {
    box-shadow: ${theme.shadows.md};
  } */

  ${({ $size }) => {
    switch ($size) {
      case 'small':
        return `
          min-height: 200px;
          max-height: 300px;
        `;
      case 'medium':
        return `
          min-height: 300px;
          max-height: 500px;
        `;
      case 'large':
        return `
          min-height: 400px;
          max-height: 700px;
        `;
      case 'auto':
      default:
        return `
          min-height: 300px;
        `;
    }
  }}
`;

const ChartHeader = styled.div`
  padding: ${theme.spacing.lg} ${theme.spacing.lg} ${theme.spacing.md};
  border-bottom: 1px solid ${theme.colors.gray[100]};
`;

const ChartDescription = styled.div`
  margin-top: ${theme.spacing.xs};
  color: ${theme.colors.gray[600]};
`;

const ChartContent = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 250px;
`;

const ChartLoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  background: ${theme.colors.gray[50]};
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${theme.colors.gray[200]};
  border-top: 4px solid ${theme.colors.primary[500]};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  background: ${theme.colors.gray[50]};
  color: ${theme.colors.error};
  text-align: center;
  padding: ${theme.spacing.xl};
`;

const ErrorTitle = styled.div`
  color: ${theme.colors.error};
  margin-bottom: ${theme.spacing.sm};
`;

const ErrorMessage = styled.div`
  color: ${theme.colors.gray[600]};
`;

// Default chart theme based on the app's theme
const defaultChartTheme: ChartTheme = {
  backgroundColor: theme.colors.white,
  gridColor: theme.colors.gray[200],
  textColor: theme.colors.gray[700],
  primaryColor: theme.colors.primary[500],
  secondaryColor: theme.colors.primary[300],
  accentColors: [
    theme.colors.primary[500],
    theme.colors.primary[400],
    theme.colors.primary[600],
    '#10b981', // success
    '#f59e0b', // warning
    '#ef4444', // error
    theme.colors.gray[500],
    theme.colors.gray[600],
  ],
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
  fontSize: 12,
};

// Helper function to convert legacy ChartData format to Recharts format
const convertToRechartsData = (data: ChartData[] | RechartsDataPoint[], chartTheme: ChartTheme = defaultChartTheme): RechartsDataPoint[] => {
  // If already in Recharts format (array of data points), return as-is
  if (data?.length > 0 && !('type' in data[0]) && !('x' in data[0] && Array.isArray((data[0] as ChartData).x))) {
    return data as RechartsDataPoint[];
  }
  
  // Convert from legacy ChartData format
  const chartData = data as ChartData[];
  if (!chartData || chartData?.length === 0) return [];

  // Get the first trace to determine structure
  const firstTrace = chartData[0];
  const traceType = firstTrace.type || 'scatter';
  
  // For line/bar/area charts, combine x and y arrays
  if (['scatter', 'bar', 'line'].includes(traceType)) {
    const xValues = firstTrace.x || [];
    if (xValues?.length === 0) return [];
    
    const result: RechartsDataPoint[] = [];
    
    // Create data points from x and y arrays
    xValues.forEach((xVal, index) => {
      const dataPoint: RechartsDataPoint = { x: xVal };
      
      // Add y values from all traces
      chartData.forEach((trace, traceIndex) => {
        const yValues = trace.y || [];
        const traceName = trace.name || `series${traceIndex}`;
        // Handle cases where y array might be shorter than x array
        dataPoint[traceName] = yValues[index] !== undefined ? yValues[index] : null;
      });
      
      result.push(dataPoint);
    });
    
    return result;
  }
  
  // For pie charts, convert values and labels
  if (traceType === 'pie') {
    const values = firstTrace.values || [];
    const labels = firstTrace.labels || [];
    
    // Filter out any invalid values
    const validData = values.map((value, index) => {
      const numValue = typeof value === 'number' ? value : parseFloat(String(value)) || 0;
  return {
        name: labels[index] || `Item ${index}`,
        value: numValue,
      };
    }).filter(item => item.value > 0); // Only include items with value > 0
    
    return validData as RechartsDataPoint[];
  }
  
  return [];
};

// Helper function to get chart type from data
const getChartType = (data: ChartData[] | RechartsDataPoint[]): 'line' | 'bar' | 'area' | 'pie' | 'scatter' => {
  if (!data || data?.length === 0) return 'line';
  
  // If Recharts format, infer from data structure
  if (!('type' in data[0])) {
    // Recharts format - check if it's pie (has 'name' and 'value' keys)
    const firstPoint = data[0] as RechartsDataPoint;
    if ('name' in firstPoint && 'value' in firstPoint && !('x' in firstPoint)) {
      return 'pie';
    }
    // Default to line for Recharts format
    return 'line';
  }
  
  // Legacy ChartData format
  const firstTrace = data[0] as ChartData;
  const traceType = firstTrace.type || 'scatter';
  
  if (traceType === 'bar') return 'bar';
  if (traceType === 'pie') return 'pie';
  
  // Check for area fill patterns
  const fill = firstTrace.fill;
  if (fill && (fill === 'tozeroy' || fill === 'tonexty' || fill === 'toself' || fill === 'tonext')) {
    return 'area';
  }
  
  // Check for fillcolor which also indicates area
  if (firstTrace.fillcolor) {
    return 'area';
  }
  
  if (traceType === 'scatter' || traceType === 'line') return 'line';
  
  return 'line';
};

// Helper function to get colors for series
const getSeriesColors = (data: ChartData[] | RechartsDataPoint[], chartTheme: ChartTheme = defaultChartTheme): string[] => {
  // If Recharts format, we can't determine colors from data structure alone
  // Return default colors based on number of series
  if (data?.length > 0 && !('type' in data[0])) {
    const rechartsData = data as RechartsDataPoint[];
    if (rechartsData?.length > 0) {
      // Count series by checking keys (excluding 'x')
      const firstPoint = rechartsData[0];
      const seriesKeys = Object.keys(firstPoint).filter(key => key !== 'x' && key !== 'name' && key !== 'value');
      return seriesKeys.map((_, index) => {
        const colorIndex = index % (chartTheme.accentColors?.length || 1);
        return chartTheme.accentColors?.[colorIndex] || chartTheme.primaryColor || '#FB6F2A';
      });
    }
  }
  
  // Legacy ChartData format
  const chartData = data as ChartData[];
  return chartData.map((trace, index) => {
    const colorIndex = index % (chartTheme.accentColors?.length || 1);
    
    // Check for explicit color in marker or line
    const markerColor = trace.marker?.color as string;
    const lineColor = trace.line?.color as string;
    
    return markerColor || lineColor || chartTheme.accentColors?.[colorIndex] || chartTheme.primaryColor || '#FB6F2A';
  });
};

// Helper function to extract series properties from data
const getSeriesProperties = (data: ChartData[] | RechartsDataPoint[]) => {
  // If Recharts format, return defaults
  if (data?.length > 0 && !('type' in data[0])) {
    const rechartsData = data as RechartsDataPoint[];
    if (rechartsData?.length > 0) {
      const firstPoint = rechartsData[0];
      const seriesKeys = Object.keys(firstPoint).filter(key => key !== 'x' && key !== 'name' && key !== 'value');
      return seriesKeys.map(() => ({
        strokeWidth: 2,
        strokeDash: undefined,
        showMarkers: false,
        markerSize: 8,
      }));
    }
  }
  
  // Legacy ChartData format
  const chartData = data as ChartData[];
  return chartData.map((trace) => {
    const traceData = trace as ChartData;
    const line = traceData.line;
    const marker = traceData.marker;
    const fill = traceData.fill;
    const fillcolor = traceData.fillcolor;
    
    return {
      strokeWidth: line?.width || 2,
      strokeDash: line?.dash,
      showMarkers: marker !== undefined && marker !== null,
      markerSize: (typeof marker?.size === 'number' ? marker.size : 8),
      fillColor: fillcolor,
      fillOpacity: fill && fill !== 'none' ? 0.3 : undefined,
    };
  });
};

// Chart container component
const ChartWrapper: React.FC<ChartContainerProps> = ({
  title,
  description,
  size = 'auto',
  loading = false,
  error,
  children,
}) => {
  if (loading) {
    return (
      <ChartContainer $size={size}>
        {(title || description) && (
          <ChartHeader>
            {title && <Typography variant="heading-large">{title}</Typography>}
            {description && (
              <ChartDescription>
                <Typography variant="body-regular">
                  {description}
                </Typography>
              </ChartDescription>
            )}
          </ChartHeader>
        )}
        <ChartLoadingContainer>
          <LoadingSpinner />
        </ChartLoadingContainer>
      </ChartContainer>
    );
  }

  if (error) {
    return (
      <ChartContainer $size={size}>
        {(title || description) && (
          <ChartHeader>
            {title && <Typography variant="heading-large">{title}</Typography>}
            {description && (
              <ChartDescription>
                <Typography variant="body-regular">
                  {description}
                </Typography>
              </ChartDescription>
            )}
          </ChartHeader>
        )}
        <ErrorContainer>
          <div>
            <ErrorTitle>
              <Typography variant="body-semibold">
                Error loading chart
              </Typography>
            </ErrorTitle>
            <ErrorMessage>
              <Typography variant="body-regular">
                {error}
              </Typography>
            </ErrorMessage>
          </div>
        </ErrorContainer>
      </ChartContainer>
    );
  }

  return (
    <ChartContainer $size={size}>
      {(title || description) && (
        <ChartHeader>
          {title && <Typography variant="heading-large">{title}</Typography>}
          {description && (
            <ChartDescription>
              <Typography variant="body-regular">
                {description}
              </Typography>
            </ChartDescription>
          )}
        </ChartHeader>
      )}
      <ChartContent>{children || null}</ChartContent>
    </ChartContainer>
  );
};

// Custom tooltip component
const CustomTooltip = ({ active, payload, label, coordinate, threshold, tooltipLabel, tooltipFormatter }: {
  active?: boolean;
  payload?: readonly any[];
  label?: string | number;
  coordinate?: { x: number; y: number };
  threshold?: number;
  tooltipLabel?: string;
  tooltipFormatter?: (value: number) => string;
}) => {
  // Helper function to format response time value (default formatter - ms/s units)
  const formatResponseTime = (valueMs: number): string => {
    if (valueMs >= 1000) {
      // Convert to seconds with 1 decimal place
      const seconds = valueMs / 1000;
      // Format to remove trailing zeros (e.g., 1.0 -> 1, 1.2 -> 1.2)
      const formatted = seconds % 1 === 0 ? seconds.toString() : seconds.toFixed(1);
      return `${formatted} s`;
    }
    return `${Math.round(valueMs)} ms`;
  };

  // Helper function to determine color based on threshold
  const getValueColor = (valueMs: number, thresholdValue?: number): string => {
    if (thresholdValue !== undefined && thresholdValue !== null) {
      if (valueMs >= thresholdValue) {
        return theme.colors.error; // Red - threshold crossed
      } else if (valueMs >= thresholdValue * 0.7) {
        return theme.colors.warning; // Yellow - warning zone (70% to threshold)
      } else {
        return theme.colors.success; // Green - optimal (below 70% of threshold)
      }
    }
    // Default to line color if no threshold
    return theme.colors.gray[900];
  };

  if (active && payload && payload?.length > 0) {
    // Select payload - prioritize points above threshold if any exist, otherwise use first payload
    let selectedPayload = payload[0];
    
    if (threshold !== undefined && threshold !== null) {
      const aboveThresholdPayload = payload.find(p => {
        const value = typeof p.value === 'number' ? p.value : parseFloat(String(p.value)) || 0;
        return value >= threshold;
      });
      if (aboveThresholdPayload) {
        selectedPayload = aboveThresholdPayload;
      }
    }
    
    const value = selectedPayload.value;
    const numericValue = typeof value === 'number' ? value : parseFloat(String(value)) || 0;
    const valueColor = getValueColor(numericValue, threshold);
    
    // Format the label - if it already has AM/PM, use it as-is; otherwise parse and format
    let dateTimeLabel = label;
    if (label && typeof label === 'string') {
      // Check if label already contains AM/PM
      const hasAMPM = /AM|PM/i.test(label);
      
      if (hasAMPM) {
        // Label already has AM/PM, use it as-is to preserve the correct value
        dateTimeLabel = label;
      } else {
        // Try to parse the date string and reformat with AM/PM
        // Common formats: "Nov 20, 12:15", "12:15", "Dec 2, 00:45"
        const dateMatch = label.match(/([A-Za-z]{3}\s+\d{1,2},\s+)?(\d{1,2}):(\d{2})/);
        if (dateMatch) {
          try {
            // Reconstruct date string for parsing
            const dateStr = dateMatch[0];
            // If it has month/day, parse as full date; otherwise use current date
            let date: Date;
            if (dateMatch[1]) {
              // Has month and day: "Nov 20, 12:15"
              date = new Date(dateStr);
            } else {
              // Just time: "12:15" - use current date with this time
              const [hours, minutes] = dateStr?.split(':').map(Number);
              date = new Date();
              date.setHours(hours, minutes, 0, 0);
            }
            
            // Format with AM/PM
            if (dateMatch[1]) {
              // Full date format: "Nov 20, 12:15 PM"
              dateTimeLabel = date.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              });
            } else {
              // Time only format: "12:15 PM"
              dateTimeLabel = date.toLocaleString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              });
            }
          } catch (e) {
            // If parsing fails, use original label
            dateTimeLabel = label;
          }
        }
      }
    }
    
    // Format value - use custom formatter if provided, otherwise use default formatResponseTime
    const displayValue = tooltipFormatter 
      ? tooltipFormatter(numericValue)
      : formatResponseTime(numericValue);
    
    return (
      <TooltipContainer>
        {dateTimeLabel && (
          <TooltipTitle>{dateTimeLabel}</TooltipTitle>
        )}
        <TooltipRow>
          <TooltipLabel>{tooltipLabel || 'Response time'}</TooltipLabel>
          <TooltipValue $color={valueColor}>
            {displayValue}
          </TooltipValue>
        </TooltipRow>
      </TooltipContainer>
    );
  }
  
  
  return null;
};

const TooltipContainer = styled.div`
  background: ${theme.colors.white};
  border: 1px solid #D9D9DC;
  border-radius: 8px;
  padding: 8px;
  box-shadow: ${theme.shadows.md};
`;

const TooltipTitle = styled.div`
  font-size: 13px;
  font-family: 'Open Sans', sans-serif;
  font-weight: ${theme.fontWeights.bold};
  color: #000000;
  margin-bottom: 10px;
`;

const TooltipRow = styled.div`
  font-size: 12px;
  color: #6E6E6E;
  display: flex;
  justify-content: space-between;
  gap: 10px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const TooltipLabel = styled.span`
  font-weight: ${theme.fontWeights.medium};
  font-family: 'Open Sans', sans-serif;
`;

const TooltipValue = styled.span<{ $color: string }>`
  font-weight: ${theme.fontWeights.bold};
  color: ${({ $color }) => $color};
`;

// Main Chart component
export const Chart = forwardRef<HTMLDivElement, ChartContainerProps & { 
  data: ChartData[] | RechartsDataPoint[];
  layout?: ChartLayout;
  config?: ChartConfig;
  chartTheme?: ChartTheme;
  className?: string;
  style?: React.CSSProperties;
  onHover?: (event: { activeLabel?: string | number; activePayload?: Array<{ name: string; value: number | string }> }) => void;
  onClick?: (event: { activeLabel?: string | number; activePayload?: Array<{ name: string; value: number | string }> }) => void;
  onError?: (error: Error) => void;
  revision?: number;
  useResizeHandler?: boolean;
  debug?: boolean;
  divId?: string;
  customTooltip?: React.ComponentType<any>;
  tooltipLabel?: string;
  tooltipFormatter?: (value: number) => string;
  tooltipData?: any; // Additional data to pass to custom tooltip
}>(
  ({
    data,
    layout = {},
    config = {},
    className,
    style,
    onHover,
    onClick,
    onError,
    revision,
    useResizeHandler = true,
    debug = false,
    divId,
    title,
    description,
    size = 'auto',
    loading = false,
    error,
    chartTheme = defaultChartTheme,
    customTooltip,
    tooltipLabel,
    tooltipFormatter,
    tooltipData,
    showLegend = false,
    ...props
  }, ref) => {
    // State for tracking hovered bar index (for opacity effect)
    const [hoveredBarIndex, setHoveredBarIndex] = useState<number | null>(null);
    
    // Convert data to Recharts format
    const rechartsData = useMemo(() => convertToRechartsData(data, chartTheme), [data, chartTheme]);
    const chartType = useMemo(() => getChartType(data), [data]);
    const seriesColors = useMemo(() => getSeriesColors(data, chartTheme), [data, chartTheme]);
    const seriesProperties = useMemo(() => getSeriesProperties(data), [data]);
    // Get series names from data
    const seriesNames = useMemo(() => {
      // If Recharts format, extract series names from data keys
      if (data?.length > 0 && !('type' in data[0])) {
        const rechartsData = data as RechartsDataPoint[];
        if (rechartsData?.length > 0) {
          const firstPoint = rechartsData[0];
          return Object.keys(firstPoint).filter(key => key !== 'x' && key !== 'name' && key !== 'value');
        }
      }
      
      // Legacy ChartData format
      const chartData = data as ChartData[];
      return chartData.map((trace: ChartData, index: number) => {
        return String(trace.name || `series${index}`);
      });
    }, [data]);

    // Extract threshold lines from layout.shapes
    const thresholdLines = useMemo(() => {
      const shapes = layout.shapes as Array<{
        type?: string;
        y0?: number;
        y1?: number;
        line?: { color?: string; width?: number; dash?: string };
      }> | undefined;
      
      if (!shapes || !Array.isArray(shapes)) return [];
      
      return shapes
        .filter(shape => shape.type === 'line' && shape.y0 === shape.y1)
        .map(shape => ({
          y: shape.y0 || shape.y1 || 0,
          stroke: shape.line?.color || '#D3072A',
          strokeWidth: shape.line?.width || 1,
          strokeDasharray: shape.line?.dash === 'dash' ? '5 5' : undefined,
        }));
    }, [layout.shapes]);


  // Error handling callback
  const handleError = useCallback((error: Error) => {
    if (debug) {
      console.error('Chart Error:', error);
    }
    onError?.(error);
  }, [debug, onError]);

    // Render chart based on type
    const renderChart = () => {
      // Map margin from legacy format (l, r, t, b) or Recharts format (top, right, left, bottom)
      const marginObj = layout.margin as { top?: number; right?: number; left?: number; bottom?: number; t?: number; r?: number; l?: number; b?: number } | undefined;
      const margin = {
        top: marginObj?.top ?? marginObj?.t ?? 20,
        right: marginObj?.right ?? marginObj?.r ?? 30,
        left: marginObj?.left ?? marginObj?.l ?? 0,
        bottom: marginObj?.bottom ?? marginObj?.b ?? 0,
      };

      const commonProps = {
        data: rechartsData,
        margin,
      };

      switch (chartType) {
        case 'line': {
          // Check if x-axis should be category type
          const isCategoryAxis = layout.xaxis?.type === 'category';
          const layoutXAxisDomain = layout.xaxis?.range as [number, number] | undefined;
          // Check if zoom is enabled from config
          const enableZoom = config.scrollZoom !== false;
          // Use layout domain
          const effectiveDomain = (layout.xaxis?.domain as [number | string, number | string] | undefined) ?? (layoutXAxisDomain ? [layoutXAxisDomain[0], layoutXAxisDomain[1]] : undefined);
          
    
          return (
            <LineChart {...commonProps}>
              <CartesianGrid 
                strokeDasharray="4 3" 
                stroke={chartTheme.gridColor || '#e5e7eb'} 
                strokeOpacity={1}
                vertical={false} 
                horizontal={true}
              />
              <XAxis
                dataKey="x"
                type={isCategoryAxis ? 'category' : 'number'}
                stroke={chartTheme.textColor}
                tick={{ fill: '#6D7280', fontSize: 12, fontFamily: 'Open Sans, sans-serif' }}
                tickLine={false}
                axisLine={false}
                domain={effectiveDomain}
                ticks={layout.xaxis?.ticks as number[] | undefined}
                tickCount={(layout.xaxis as { tickCount?: number })?.tickCount ?? 4}
                interval={isCategoryAxis ? 'preserveStartEnd' : undefined}
                allowDecimals={(layout.xaxis as { allowDecimals?: boolean })?.allowDecimals ?? false}
                allowDataOverflow={enableZoom}
                minTickGap={35}
                tickMargin={10}
                tickFormatter={(layout.xaxis as { tickFormatter?: (value: string | number) => string })?.tickFormatter}
                label={(layout.xaxis as { title?: { text?: string } })?.title?.text ? {
                  value: (layout.xaxis as { title?: { text?: string } })?.title?.text,
                  position: 'bottom',
                  offset: 10,
                  style: { textAnchor: 'middle', fill: '#6E6E6E', fontSize: 12, fontFamily: 'Open Sans, sans-serif' }
                } : undefined}
                padding={{ left:5,right: 5 }}
              />
              <YAxis
                stroke={chartTheme.textColor}
                tick={{ fill: '#6D7280', fontSize: 12, fontFamily: 'Open Sans, sans-serif' }}
                tickLine={false}
                axisLine={false}
                domain={layout.yaxis?.domain as [number | string, number | string] | undefined ?? (layout.yaxis?.range ? [layout.yaxis.range[0], layout.yaxis.range[1]] : undefined)}
                allowDataOverflow={enableZoom}
                ticks={layout.yaxis?.ticks as number[] | undefined ?? (layout.yaxis?.tickvals as number[] | undefined)}
                tickCount={(layout.yaxis?.ticks || layout.yaxis?.tickvals) ? undefined : ((layout.yaxis as { tickCount?: number })?.tickCount ?? 1)}
                allowDecimals={(layout.yaxis as { allowDecimals?: boolean })?.allowDecimals}
                tickFormatter={(value) => {
                  // Check if there's a custom tick formatter in layout (from LineChart)
                  if (layout.yaxis?.ticktext && layout.yaxis?.tickvals) {
                    const tickVals = layout.yaxis.tickvals as number[];
                    const tickTexts = layout.yaxis.ticktext as string[];
                    // Find the exact or closest matching tick value
                    // First try exact match with small tolerance
                    let index = tickVals.findIndex(v => Math.abs(v - value) < 0.01);
                    if (index >= 0) return tickTexts[index];
                    // If no exact match, find closest value
                    index = tickVals.reduce((closest, val, idx) => {
                      return Math.abs(val - value) < Math.abs(tickVals[closest] - value) ? idx : closest;
                    }, 0);
                    // Use closest if within reasonable range (5% of the value or 10 units, whichever is smaller)
                    const tolerance = Math.min(Math.abs(value) * 0.05, 10);
                    if (Math.abs(tickVals[index] - value) <= tolerance) {
                      return tickTexts[index];
                    }
                    // If we have ticktext but no match, check if any ticktext contains "ms" or "s" to infer formatting
                    const hasUnitFormatting = tickTexts.some(text => typeof text === 'string' && (text.includes('ms') || text.includes('s')));
                    if (hasUnitFormatting) {
                      // Apply same formatting logic: < 1000ms -> "X ms", >= 1000ms -> "X.X s"
                      if (value < 1000) {
                        return `${Math.round(value)} ms`;
                      } else {
                        const seconds = value / 1000;
                        const formatted = seconds % 1 === 0 ? seconds.toString() : seconds.toFixed(1);
                        return `${formatted} s`;
                      }
                    }
                  }
                  // Use suffix if provided
                  const suffix = layout.yaxis?.ticksuffix as string | undefined;
                  return suffix ? `${value}${suffix}` : `${value}`;
                }}
              />
              <Tooltip 
                content={customTooltip ? (props: any) => {
                  const TooltipComponent = customTooltip;
                  return <TooltipComponent {...props} threshold={thresholdLines[0]?.y} tooltipLabel={tooltipLabel} tooltipFormatter={tooltipFormatter} tooltipData={tooltipData} />;
                } : ((props) => <CustomTooltip {...props} threshold={thresholdLines[0]?.y} tooltipLabel={tooltipLabel} tooltipFormatter={tooltipFormatter} />)} 
                cursor={false}
                shared={customTooltip ? true : false}
                allowEscapeViewBox={{ x: false, y: false }}
              />
              {/* Render threshold lines */}
              {thresholdLines.map((threshold, index) => (
                <ReferenceLine
                  key={`threshold-${index}`}
                  y={threshold.y}
                  stroke={threshold.stroke}
                  strokeWidth={threshold.strokeWidth}
                  strokeDasharray={threshold.strokeDasharray}
                />
              ))}
              {seriesNames.map((name: string, index: number) => {
                const props = seriesProperties[index] || { strokeWidth: 2, strokeDash: undefined, showMarkers: false, markerSize: 8 };
                const strokeDasharray = props.strokeDash === 'dash' ? '5 5' : 
                                       props.strokeDash === 'dot' ? '2 2' : 
                                       props.strokeDash === 'dashdot' ? '5 2 2 2' : undefined;
                
                return (
                  <Line
                    key={name}
                    type="monotone"
                    dataKey={name}
                    stroke={seriesColors[index]}
                    strokeWidth={props.strokeWidth}
                    strokeDasharray={strokeDasharray}
                    dot={props.showMarkers ? { r: props.markerSize / 2 } : false}
                    activeDot={{ r: 6, strokeWidth: 2 }}
                  />
                );
              })}
              {/* Add brush for zoom functionality */}
              {enableZoom && !isCategoryAxis && (
                <Brush
                  dataKey="x"
                  height={config.brushHeight ?? 30}
                  stroke={chartTheme.gridColor}
                  fill={theme.colors.gray[50]}
                  tickFormatter={(value) => {
                    if (typeof value === 'number') return value.toString();
                    return String(value);
                  }}
                />
              )}
            </LineChart>
          );
        }
        case 'bar': {
          const enableZoom = config.scrollZoom !== false;
          const barSize = layout.barSize as number | undefined;
          const maxBarSize = layout.maxBarSize as number | undefined;
          const barCategoryGap = layout.barCategoryGap as number | string | undefined;
          const barGap = layout.barGap as number | string | undefined;
          // Check if X-axis is category type (for vertical bars)
          // For category axes, Recharts uses maxBarSize; for linear axes, it uses barSize
          const isCategoryAxis = (layout.xaxis as { type?: string })?.type === 'category';
          // Use maxBarSize for category axes, barSize for linear axes
          const barSizeProps = isCategoryAxis && maxBarSize !== undefined 
            ? { maxBarSize } 
            : barSize !== undefined 
              ? { barSize } 
              : {};
          // Check if data is empty (no categories or all series have empty/zero data)
          const isEmpty = useMemo(() => {
            if (!data || data.length === 0) return true;
            // Check if it's Recharts format
            if (data.length > 0 && !('type' in data[0])) {
              const rechartsData = data as RechartsDataPoint[];
              if (rechartsData.length === 0) return true;
              // Check if all values are 0 or null/undefined
              const allValues = rechartsData.flatMap(point => {
                return Object.entries(point)
                  .filter(([key]) => key !== 'x' && key !== 'name' && key !== 'value')
                  .map(([, value]) => typeof value === 'number' ? value : 0);
              });
              return allValues.length === 0 || allValues.every(val => val === 0 || val === null || val === undefined);
            }
            // Legacy format
            const chartData = data as ChartData[];
            if (chartData.length === 0) return true;
            // Check if all y values are 0 or empty
            const allYValues = chartData.flatMap(trace => {
              const yValues = Array.isArray(trace.y) ? trace.y : [];
              return yValues.map(val => typeof val === 'number' ? val : 0);
            });
            return allYValues.length === 0 || allYValues.every(val => val === 0 || val === null || val === undefined);
          }, [data]);
          
          return (
            <BarChart 
              {...commonProps} 
              {...barSizeProps}
              {...(barCategoryGap !== undefined ? { barCategoryGap } : {})}
              {...(barGap !== undefined ? { barGap } : {})}
            >
              <CartesianGrid 
                strokeDasharray="12 12" 
                stroke={chartTheme.gridColor || '#D9D9DC'} 
                strokeWidth={1}
                strikethroughThickness={1}
                strokeOpacity={1}
                vertical={false} 
                horizontal={true}
              />
              <XAxis
                dataKey="x"
                stroke={chartTheme.textColor}
                tick={(layout.xaxis as { hideTicks?: boolean })?.hideTicks ? false : { 
                  fill: (layout.xaxis as { tickfont?: { color?: string } })?.tickfont?.color || '#6D7280', 
                  fontSize: 12, 
                  fontFamily: 'Open Sans, sans-serif' 
                }}
                tickLine={false}
                axisLine={false}
                allowDataOverflow={enableZoom}
                domain={isEmpty && !layout.xaxis?.domain && (layout.xaxis as { type?: string })?.type === 'category'
                  ? [0, 3] as [number, number] // Default domain for category axis when empty
                  : (layout.xaxis?.domain as [number | string, number | string] | undefined)}
                ticks={layout.xaxis?.ticks as number[] | undefined}
                tickCount={isEmpty && !layout.xaxis?.domain && (layout.xaxis as { type?: string })?.type === 'category'
                  ? 4 // Default tick count when empty
                  : ((layout.xaxis as { tickCount?: number })?.tickCount ?? 4)}
                interval="preserveStartEnd"
                allowDecimals={(layout.xaxis as { allowDecimals?: boolean })?.allowDecimals}
                tickMargin={(layout.xaxis as { tickMargin?: number })?.tickMargin ?? 10}
                minTickGap={35}
                label={(layout.xaxis as { title?: { text?: string; font?: { size?: number; color?: string; family?: string } } })?.title ? (() => {
                  const xAxisTitle = (layout.xaxis as { title?: { text?: string } })?.title;
                  const xAxisTitleText = xAxisTitle?.text || '';
                  // Only show label if text is not empty
                  if (!xAxisTitleText || xAxisTitleText?.trim() === '') {
                    return undefined;
                  }
                  return {
                    value: xAxisTitleText,
                    position: 'insideBottom',
                    offset: -20,
                    style: {
                      fill: (layout.xaxis as { title?: { font?: { color?: string } } })?.title?.font?.color || '#6E6E6E',
                      fontSize: (layout.xaxis as { title?: { font?: { size?: number } } })?.title?.font?.size || 12,
                      fontFamily: (layout.xaxis as { title?: { font?: { family?: string } } })?.title?.font?.family || 'Open Sans, sans-serif',
                    },
                  };
                })() : undefined}
              />
              <YAxis
                stroke={chartTheme.textColor}
                tick={(layout.yaxis as { hideTicks?: boolean })?.hideTicks ? false : { 
                  fill: (layout.yaxis as { tickfont?: { color?: string } })?.tickfont?.color || '#6D7280', 
                  fontSize: 12, 
                  fontFamily: 'Open Sans, sans-serif' 
                }}
                tickLine={false}
                axisLine={false}
                allowDataOverflow={enableZoom}
                domain={isEmpty && !layout.yaxis?.domain 
                  ? [0, 10] as [number, number] // Default domain when empty to show grid lines
                  : (layout.yaxis?.domain as [number | string, number | string] | undefined)}
                ticks={layout.yaxis?.ticks as number[] | undefined}
                tickCount={isEmpty && !layout.yaxis?.domain 
                  ? 6 // Default tick count when empty
                  : (layout.yaxis as { tickCount?: number })?.tickCount}
                allowDecimals={(layout.yaxis as { allowDecimals?: boolean })?.allowDecimals}
                tickFormatter={(layout.yaxis as { tickFormatter?: (value: number) => string })?.tickFormatter}
                tickMargin={(layout.yaxis as { tickMargin?: number })?.tickMargin ?? 4}
                label={(layout.yaxis as { title?: { text?: string; font?: { size?: number; color?: string; family?: string }; offset?: number } })?.title ? {
                  value: (layout.yaxis as { title?: { text?: string } })?.title?.text || '',
                  angle: -90,
                  position: 'insideLeft',
                  offset: (layout.yaxis as { title?: { offset?: number } })?.title?.offset ?? 10,
                  style: {
                    fill: (layout.yaxis as { title?: { font?: { color?: string } } })?.title?.font?.color || '#6E6E6E',
                    fontSize: (layout.yaxis as { title?: { font?: { size?: number } } })?.title?.font?.size || 12,
                    fontFamily: (layout.yaxis as { title?: { font?: { family?: string } } })?.title?.font?.family || 'Open Sans, sans-serif',
                  },
                } : undefined}
              />
              <Tooltip 
                content={customTooltip ? (props: any) => {
                  const TooltipComponent = customTooltip;
                  // Track hover state for opacity effect
                  if (props.active && props.payload && props.payload.length > 0 && props.label !== undefined) {
                    const dataIndex = rechartsData?.findIndex((d: any) => String(d.x) === String(props.label)) ?? null;
                    if (dataIndex !== null && dataIndex >= 0 && dataIndex !== hoveredBarIndex) {
                      setHoveredBarIndex(dataIndex);
                    }
                  } else if (!props.active && hoveredBarIndex !== null) {
                    setHoveredBarIndex(null);
                  }
                  return <TooltipComponent {...props} threshold={thresholdLines[0]?.y} tooltipLabel={tooltipLabel} tooltipFormatter={tooltipFormatter} tooltipData={tooltipData} />;
                } : ((props) => {
                  // Track hover state for opacity effect
                  if (props.active && props.payload && props.payload.length > 0 && props.label !== undefined) {
                    const dataIndex = rechartsData?.findIndex((d: any) => String(d.x) === String(props.label)) ?? null;
                    if (dataIndex !== null && dataIndex >= 0 && dataIndex !== hoveredBarIndex) {
                      setHoveredBarIndex(dataIndex);
                    }
                  } else if (!props.active && hoveredBarIndex !== null) {
                    setHoveredBarIndex(null);
                  }
                  return <CustomTooltip {...props} threshold={thresholdLines[0]?.y} tooltipLabel={tooltipLabel} tooltipFormatter={tooltipFormatter} />;
                })} 
                cursor={false}
                shared={(layout as { barmode?: string })?.barmode === 'stack' || seriesNames.length > 1}
                allowEscapeViewBox={{ x: false, y: false }}
              />
              <CartesianGrid 
                strokeDasharray="12 12" 
                stroke={chartTheme.gridColor || '#D9D9DC'} 
                strokeWidth={1}
                strikethroughThickness={1}
                strokeOpacity={1}
                vertical={false} 
                horizontal={true}
              />
              {seriesNames.map((name: string, index: number) => {
                // Check if stacking is enabled via layout.barmode
                const barmode = (layout as { barmode?: string })?.barmode;
                const isStacked = barmode === 'stack';
                
                // For stacked bars: only top bar (last series) gets radius on top corners
                // Bottom and middle bars get no radius
                const getBarRadius = (): [number, number, number, number] => {
                  if (isStacked) {
                    // Last series (top bar) gets radius on top corners only
                    if (index === seriesNames.length - 1) {
                      return [4, 4, 0, 0]; // Top corners only
                    }
                    // All other bars (bottom and middle) get no radius
                    return [0, 0, 0, 0];
                  }
                  // Non-stacked bars: no radius (zero border radius)
                  return [0, 0, 0, 0];
                };
                
                return (
                  <Bar 
                    key={name} 
                    dataKey={name} 
                    fill={seriesColors[index]} 
                    radius={getBarRadius()}
                    stackId={isStacked ? 'stack' : undefined}
                    onClick={onClick ? (data: any, barIndex: number, e: React.MouseEvent) => {
                    // Convert Recharts Bar onClick format to Chart onClick format
                    // In Recharts Bar, data contains: { x: category, [seriesName]: value, ... }
                    // The 'name' variable here is the series name (dataKey)
                    // The barIndex parameter is the index of the data point in the array
                    // data.x might be a number or string - we need to get the actual category name
                    let activeLabel: string | number | undefined = data?.x;
                    
                    // If x is a number or not a valid category, try to get it from rechartsData using barIndex
                    if ((typeof activeLabel === 'number' || activeLabel == null) && rechartsData && rechartsData?.length > barIndex) {
                      // Get the actual data point from rechartsData using the barIndex
                      const dataPoint = rechartsData[barIndex];
                      const xValue = dataPoint?.x;
                      // Only use it if it's a valid string or number (not null)
                      if (xValue != null && (typeof xValue === 'string' || typeof xValue === 'number')) {
                        activeLabel = xValue;
                      }
                    }
                    
                    // Ensure activeLabel is a string or number (not null)
                    if (activeLabel == null) {
                      activeLabel = undefined;
                    } else if (typeof activeLabel !== 'string' && typeof activeLabel !== 'number') {
                      // Convert to string if it's a Date or other type
                      activeLabel = String(activeLabel);
                    }
                    
                    const value = data?.[name] || 0; // Value for this series
                    const activePayload = [{
                      name: name, // Series name (e.g., "domain0", "domain1")
                      value: value,
                      dataKey: name,
                      payload: data || {},
                    }];
                    onClick({
                      activeLabel,
                      activePayload,
                    });
                  } : undefined}
                  {...barSizeProps}
                />
                );
              })}
            </BarChart>
          );
        }
        case 'area': {
          const enableZoom = config.scrollZoom !== false;
          const areaGradient = (layout as { areaGradient?: { id: string; from: { color: string; opacity: number }; to: { color: string; opacity: number } } }).areaGradient;
          return (
            <AreaChart {...commonProps}>
              {areaGradient && (
                <defs>
                  <linearGradient id={areaGradient.id} x1="0" y1="0" x2="0" y2="1" spreadMethod="pad">
                    <stop offset="0%" stopColor={areaGradient.from.color} stopOpacity={areaGradient.from.opacity} />
                    <stop offset="100%" stopColor={areaGradient.to.color} stopOpacity={areaGradient.to.opacity} />
                  </linearGradient>
                </defs>
              )}
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke={chartTheme.gridColor || '#e5e7eb'} 
                strokeOpacity={1}
                vertical={false} 
                horizontal={true}
              />
              <XAxis
                dataKey="x"
                stroke={chartTheme.textColor}
                tick={{ fill: '#6D7280', fontSize: 12, fontFamily: 'Open Sans, sans-serif' }}
                tickLine={false}
                axisLine={false}
                allowDataOverflow={enableZoom}
                domain={layout.xaxis?.domain as [number | string, number | string] | undefined}
                ticks={layout.xaxis?.ticks as number[] | undefined}
                tickCount={(layout.xaxis as { tickCount?: number })?.tickCount ?? 4}
                interval="preserveStartEnd"
                allowDecimals={(layout.xaxis as { allowDecimals?: boolean })?.allowDecimals}
                tickMargin={4}
              />
              <YAxis
                stroke={chartTheme.textColor}
                tick={{ fill: '#6D7280', fontSize: 12, fontFamily: 'Open Sans, sans-serif' }}
                tickLine={false}
                axisLine={false}
                allowDataOverflow={enableZoom}
                domain={layout.yaxis?.domain as [number | string, number | string] | undefined}
                ticks={layout.yaxis?.ticks as number[] | undefined}
                tickCount={(layout.yaxis as { tickCount?: number })?.tickCount}
                allowDecimals={(layout.yaxis as { allowDecimals?: boolean })?.allowDecimals}
              />
              <Tooltip 
                content={(props) => <CustomTooltip {...props} threshold={thresholdLines[0]?.y} tooltipLabel={tooltipLabel} tooltipFormatter={tooltipFormatter} />} 
                cursor={false}
                shared={false}
                allowEscapeViewBox={{ x: false, y: false }}
              />
              {seriesNames.map((name: string, index: number) => {
                const props = seriesProperties[index] || { strokeWidth: 2, strokeDash: undefined, showMarkers: false, markerSize: 8, fillColor: undefined, fillOpacity: undefined };
                const fillColor = ('fillColor' in props ? props.fillColor : undefined) || seriesColors[index];
                const fillOpacity = ('fillOpacity' in props ? props.fillOpacity : undefined) || 0.6;
                
                return (
                  <Area
                    key={name}
                    type="monotone"
                    dataKey={name}
                    stroke={seriesColors[index]}
                    strokeWidth={props.strokeWidth}
                    fill={fillColor}
                    fillOpacity={fillOpacity}
                    dot={props.showMarkers ? { r: props.markerSize / 2 } : false}
                    activeDot={{ r: 8, strokeWidth: 2 }}
                  />
                );
              })}
            </AreaChart>
          );
        }
        case 'pie': {
          // Check if this is a donut chart (has hole property)
          const firstTrace = (data as ChartData[])[0];
          const holeSize = firstTrace?.hole as number | undefined;
          const innerRadius = holeSize ? `${(holeSize * 100).toFixed(0)}%` : 0;
          
          // Get colors from marker: support both marker.colors and marker.color (array)
          const markerObj = firstTrace?.marker as { colors?: string[]; color?: string | string[] } | undefined;
          const markerColors = markerObj?.colors ?? (Array.isArray(markerObj?.color) ? markerObj.color : undefined);
          const colorsToUse = markerColors && markerColors?.length > 0 ? markerColors : seriesColors;
          
          // Check if we have valid data
          if (!rechartsData || rechartsData?.length === 0) {
            return (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: theme.colors.gray[600] }}>
                No data available
              </div>
            );
          }
          
          // Calculate chart dimensions
          const pieChartWidth = layout.width || 700;
          const pieChartHeight = layout.height || 350;
          
          return (
            <PieChart width={pieChartWidth} height={pieChartHeight} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
              <Pie
                data={rechartsData}
                cx={pieChartWidth / 2}
                cy={(pieChartHeight - 60) / 2}
                labelLine={false}
                label={false}
                outerRadius={Math.min(pieChartWidth, pieChartHeight - 60) * 0.45}
                innerRadius={innerRadius ? Math.min(pieChartWidth, pieChartHeight - 60) * 0.45 * parseFloat(innerRadius) / 100 : 0}
                fill="#8884d8"
                dataKey="value"
              >
                {rechartsData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={colorsToUse[index % colorsToUse?.length]} />
                ))}
              </Pie>
              <Tooltip 
                content={({ active, payload }) => {
                  if (active && payload && payload?.length > 0) {
                    const data = payload[0];
                    return (
                      <TooltipContainer>
                        <TooltipTitle>{data.name}</TooltipTitle>
                        <TooltipRow>
                          <TooltipLabel>Value</TooltipLabel>
                          <TooltipValue $color={theme.colors.gray[900]}>
                            {data.value}
                          </TooltipValue>
                        </TooltipRow>
                      </TooltipContainer>
                    );
                  }
                  return null;
                }}
                cursor={false}
                shared={false}
                allowEscapeViewBox={{ x: false, y: false }}
              />
              {showLegend && (
                <Legend 
                  wrapperStyle={{ paddingTop: '10px', paddingLeft: 0, paddingRight: 0 }}
                  iconType="circle"
                  formatter={(value) => value}
                  align="center"
                  verticalAlign="bottom"
                  layout="horizontal"
                  style={{ fontSize: '10px', fontFamily: 'Open Sans, sans-serif', fontWeight: 500, color: '#4B5563' }}
                />
              )}
            </PieChart>
          );
        }
        case 'scatter': {
          const enableZoom = config.scrollZoom !== false;
          return (
            <ScatterChart {...commonProps}>
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke={chartTheme.gridColor || '#e5e7eb'} 
                strokeOpacity={1}
                vertical={false} 
                horizontal={true}
              />
              <XAxis
                dataKey="x"
                stroke={chartTheme.textColor}
                tick={{ fill: '#6D7280', fontSize: 12, fontFamily: 'Open Sans, sans-serif' }}
                tickLine={false}
                axisLine={false}
                allowDataOverflow={enableZoom}
                domain={layout.xaxis?.domain as [number | string, number | string] | undefined}
                ticks={layout.xaxis?.ticks as number[] | undefined}
                tickCount={(layout.xaxis as { tickCount?: number })?.tickCount ?? 4}
                interval="preserveStartEnd"
                allowDecimals={(layout.xaxis as { allowDecimals?: boolean })?.allowDecimals}
                tickMargin={4}
              />
              <YAxis
                stroke={chartTheme.textColor}
                tick={{ fill: '#6D7280', fontSize: 12, fontFamily: 'Open Sans, sans-serif' }}
                tickLine={false}
                axisLine={false}
                allowDataOverflow={enableZoom}
                domain={layout.yaxis?.domain as [number | string, number | string] | undefined}
                ticks={layout.yaxis?.ticks as number[] | undefined}
                tickCount={(layout.yaxis as { tickCount?: number })?.tickCount}
                allowDecimals={(layout.yaxis as { allowDecimals?: boolean })?.allowDecimals}
              />
              <Tooltip 
                content={(props) => <CustomTooltip {...props} threshold={thresholdLines[0]?.y} tooltipLabel={tooltipLabel} tooltipFormatter={tooltipFormatter} />} 
                cursor={false}
                shared={false}
                allowEscapeViewBox={{ x: false, y: false }}
              />
              {seriesNames.map((name: string, index: number) => (
                <Scatter
                  key={name}
                  name={name}
                  data={rechartsData}
                  fill={seriesColors[index]}
                />
              ))}
            </ScatterChart>
          );
        }
        default:
          return null;
      }
    };

    // Get height from size prop or layout - default to 400px if not specified
    const chartHeight = layout.height || (size === 'small' ? 300 : size === 'medium' ? 400 : size === 'large' ? 500 : 400);

    // Check if we have data to render
    if (!rechartsData || rechartsData?.length === 0) {
      return (
        <ChartWrapper
          title={title}
          description={description}
          size={size}
          loading={loading}
          error={error || 'No data available'}
        />
      );
    }

    // ResponsiveContainer needs an explicit height in pixels
    // Use layout height if available, otherwise use size-based default
    const containerHeight = chartHeight;

    // For pie charts, don't use ResponsiveContainer - use fixed dimensions directly
    if (chartType === 'pie') {
    return (
      <ChartWrapper
        title={title}
        description={description}
        size={size}
        loading={loading}
        error={error}
      >
          <div 
            ref={ref} 
            className={className} 
            style={{ ...style, width: '100%', height: `${containerHeight}px`, minHeight: `${containerHeight}px`, display: 'flex', alignItems: 'center', justifyContent: 'center' }} 
            {...props}
          >
            {renderChart()}
          </div>
        </ChartWrapper>
      );
    }

    return (
      <ChartWrapper
        title={title}
        description={description}
        size={size}
        loading={loading}
        error={error}
      >
        <div 
          ref={ref} 
          className={className} 
          style={{ ...style, height: `${containerHeight}px`, minHeight: `${containerHeight}px` }} 
          {...props}
        >
          <ResponsiveContainer width="100%" height={containerHeight}>
            {renderChart()}
          </ResponsiveContainer>
        </div>
      </ChartWrapper>
    );
  }
);

Chart.displayName = 'Chart';

// Export helper functions and theme
export { convertToRechartsData, getChartType, getSeriesColors, defaultChartTheme };
export type { ChartTheme };

